import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { CHI_SONO, IMAGES } from "../data/content";
import { ArrowRight } from "lucide-react";

export default function ChiSono() {
  useSeo(
    "Chi sono — Susanna Paoletti, Psicologa e Psicoterapeuta a Macerata",
    "Formazione ed esperienza di Susanna Paoletti, psicologa e psicoterapeuta a Macerata: psicoterapia cognitivo-comportamentale, EMDR e valutazione neuropsicologica."
  );
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <>
      <PageHeader
        eyebrow="Chi sono"
        title="Ascoltare prima, comprendere insieme"
        subtitle="Competenza e ascolto al servizio di un percorso costruito sulla persona."
      />

      <section className="py-20 md:py-28" data-testid="chi-sono-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 order-1 lg:order-none">
            <div ref={ref} className="lg:sticky lg:top-28">
              <div className="overflow-hidden rounded-2xl bg-sand" style={{ borderRadius: "1.5rem 1.5rem 50% 50% / 1.5rem 1.5rem 22% 22%" }}>
                <motion.img
                  src={IMAGES.portrait}
                  alt="Susanna Paoletti nel suo studio a Macerata"
                  style={{ y }}
                  className="w-full h-[460px] md:h-[600px] object-cover object-top will-change-transform"
                  loading="lazy"
                />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="bg-sand border border-border rounded-xl p-4">
                  <p className="font-serif text-3xl text-ink">110/110</p>
                  <p className="text-stone mt-1">Laurea in Psicologia, 2001</p>
                </div>
                <div className="bg-sand border border-border rounded-xl p-4">
                  <p className="font-serif text-3xl text-ink">30 e lode</p>
                  <p className="text-stone mt-1">Specializzazione, 2007</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-7">
              {CHI_SONO.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className={i === 0 ? "font-serif text-2xl md:text-3xl text-ink leading-snug" : "text-lg text-stone leading-relaxed"}>
                    {p}
                  </p>
                </Reveal>
              ))}
              <Reveal delay={0.1}>
                <div className="pt-6 flex flex-col sm:flex-row gap-4">
                  <PillButton to="/formazione" testid="chi-sono-formazione">
                    Formazione ed esperienza <ArrowRight size={18} />
                  </PillButton>
                  <PillButton to="/contatti" variant="outline" testid="chi-sono-contatti">
                    Richiedi un colloquio
                  </PillButton>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
